import { useCallback, useMemo, useRef, useState } from 'react'

import {
  useDomain,
  useQuery,
} from '@tribeplatform/react-components/common/hooks'
import { Form, FormRef } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  useAuthMember,
  processAuthResponse,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { getRedirectParamsFromQuery } from '../../../utils/auth.utils.js'
import {
  MEMBER_ID_FORM_KEY,
  REDIRECT_PATH_FORM_KEY,
  VERIFICATION_CODE_FORM_KEY,
} from '../constants.js'
import { useResendVerificationCode } from '../hooks/useResendVerificationCode.js'

type EmailVerificationFormData = {
  memberId: string
  verificationCode: string
  redirect?: string
}

export const EmailVerificationForm = () => {
  const { $t } = useI18n()
  const { data: member, isGuest } = useAuthMember()
  const { getResourceUrl } = useDomain()
  const { memberId: memberIdQuery, ...query } = useQuery<{
    memberId?: string
  }>()

  const redirectPath = useMemo(() => getRedirectParamsFromQuery(query), [query])

  const memberId = memberIdQuery || member?.id

  const { onResend, isResending, codeResent } = useResendVerificationCode()

  const emailVerificationApiPath = getResourceUrl('/api/auth/verify')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const onSubmit = useCallback(
    async (data: EmailVerificationFormData) => {
      setIsSubmitting(true)
      try {
        await processAuthResponse(emailVerificationApiPath, data)
      } catch (e) {
        setIsSubmitting(false)
        throw e
      }
    },
    [emailVerificationApiPath],
  )

  const formRef = useRef<FormRef>()

  return (
    <Form onSubmit={onSubmit} action={emailVerificationApiPath} ref={formRef}>
      {methods => (
        <Form.Layout>
          <p>
            {isGuest ? (
              <T
                defaultMessage="We have sent you a verification code to your email. Please enter the code here."
                id="Auth.VerifyAccount.EmailNotice.Guest"
              />
            ) : (
              <T
                defaultMessage="We have sent a verification code to your email at {email}. Please enter the code here."
                id="Auth.VerifyAccount.EmailNotice.Member"
                values={{
                  email: <strong>{member.email}</strong>,
                }}
              />
            )}
          </p>
          <Form.Input
            name={REDIRECT_PATH_FORM_KEY}
            value={redirectPath}
            hidden
            readOnly
          />
          <Form.Input
            name={MEMBER_ID_FORM_KEY}
            value={memberId}
            readOnly
            hidden
          />
          <Form.Input
            name={VERIFICATION_CODE_FORM_KEY}
            label={$t({
              defaultMessage: 'Verification code',
              id: 'Auth.VerifyAccount.Code.Label',
            })}
            placeholder={$t({
              defaultMessage: '5-digit code',
              id: 'Auth.VerifyAccount.Code.Placeholder',
            })}
            autoComplete="off"
            autoFocus
            disabled={isSubmitting}
            onChange={e => {
              if (e.target.value.length === 5) {
                methods.submitForm({
                  [MEMBER_ID_FORM_KEY]: memberId,
                  [VERIFICATION_CODE_FORM_KEY]: e.target.value,
                  [REDIRECT_PATH_FORM_KEY]: redirectPath,
                })
              }
            }}
            validation={{
              required: $t({
                defaultMessage: 'Verification code is required.',
                id: 'Auth.VerifyAccount.CodeRequired',
              }),
            }}
            helperText={$t({
              defaultMessage:
                'If you have not received the code, use the "Resend" button below to get a new code.',
              id: 'Auth.VerifyAccount.ResendHelperText',
            })}
          />

          <Form.Actions>
            <Button type="submit" size="lg" loading={isSubmitting}>
              <T defaultMessage="Verify" id="Generics.Verify" />
            </Button>
            {!codeResent && (
              <Button
                size="lg"
                disabled={isSubmitting || isResending}
                variant="secondaryNeutral"
                onClick={onResend}
              >
                <T defaultMessage="Resend" id="Generics.Resend" />
              </Button>
            )}
          </Form.Actions>
        </Form.Layout>
      )}
    </Form>
  )
}
